'use client'

import { useEffect, useRef } from 'react'
import { createPortal } from 'react-dom'
import clsx from 'clsx'
import { BaseModalProps } from '@/types/modal'
import styles from './Modal.module.scss'

export interface ModalProps extends BaseModalProps {
  /**
   * Dimensione della modale
   */
  size?: 'sm' | 'md' | 'lg' | 'xl' | 'full'

  /**
   * Titolo della modale
   */
  title?: string

  /**
   * Impedisce la chiusura quando si clicca fuori dalla modale
   */
  preventOutsideClose?: boolean

  /**
   * Contenuto del footer (tipicamente pulsanti)
   */
  footer?: React.ReactNode

  /**
   * Classi aggiuntive per la modale
   */
  className?: string

  /**
   * Classi aggiuntive per il contenuto
   */
  contentClassName?: string
}

const Modal = ({
  isOpen,
  onClose,
  size = 'md',
  title,
  preventOutsideClose = false,
  children,
  footer,
  className,
  contentClassName,
}: ModalProps) => {
  const modalRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!isOpen) return

    // Salva il focus attuale per ripristinarlo alla chiusura
    const previousActiveElement = document.activeElement as HTMLElement

    // Imposta il focus sulla modale
    if (modalRef.current) {
      modalRef.current.focus()
    }

    // Gestione del tasto ESC per chiudere la modale
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !preventOutsideClose) {
        onClose()
      }
    }

    // Impedisci lo scrolling del body quando la modale è aperta
    document.body.style.overflow = 'hidden'

    // Aggiungi event listener
    document.addEventListener('keydown', handleKeyDown)

    // Cleanup
    return () => {
      document.body.style.overflow = ''
      document.removeEventListener('keydown', handleKeyDown)

      // Ripristina il focus
      if (previousActiveElement) {
        previousActiveElement.focus()
      }
    }
  }, [isOpen, onClose, preventOutsideClose])

  // Gestisce il click sull'overlay
  const handleOverlayClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (
      modalRef.current &&
      !modalRef.current.contains(e.target as Node) &&
      !preventOutsideClose
    ) {
      onClose()
    }
  }

  // Early return se la modale non è aperta
  if (!isOpen) return null

  // Usa createPortal per renderizzare la modale alla fine del body
  return createPortal(
    <div
      className={styles.overlay}
      onClick={handleOverlayClick}
      aria-modal='true'
      role='dialog'
      aria-hidden='false'
    >
      <div
        ref={modalRef}
        className={clsx(
          styles.modal,
          styles[`size${size.charAt(0).toUpperCase() + size.slice(1)}`],
          className
        )}
        tabIndex={-1}
      >
        {title ? (
          <div className={styles.header}>
            <h3 className={styles.title}>{title}</h3>
            <button
              className={styles.closeButton}
              onClick={onClose}
              aria-label='Chiudi modale'
            >
              ✕
            </button>
          </div>
        ) : (
          <div className={styles.headerMinimal}>
            <button
              className={styles.closeButton}
              onClick={onClose}
              aria-label='Chiudi modale'
            >
              ✕
            </button>
          </div>
        )}

        <div className={clsx(styles.content, contentClassName)}>{children}</div>

        {footer && <div className={styles.footer}>{footer}</div>}
      </div>
    </div>,
    document.body
  )
}

export default Modal
